import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { capitalize } from "../../utils/String";
import { ReactComponent as ArrowLeftIcon } from "../../assets/icons/arrow-left.svg";
import { ReactComponent as ArrowRightIcon } from "../../assets/icons/arrow-right.svg";
import backgroundImage0 from "../../assets/attributes/Background/0.png";
import backgroundImage1 from "../../assets/attributes/Background/1.png";
import backgroundImage2 from "../../assets/attributes/Background/2.png";
import backgroundImage3 from "../../assets/attributes/Background/3.png";
import backgroundImage4 from "../../assets/attributes/Background/4.png";
import backgroundImage5 from "../../assets/attributes/Background/5.png";
import styles from "./Generator.module.scss";
import Footer from "../Footer";
import Navbar from "../Navbar";
import Gminator from "./Gminator";
import ContractInfo from "../../ContractInfo";

enum Attribute {
  "Background" = "Background",
  "Base" = "Base",
  "Clothes" = "Clothes",
  "Face" = "Face",
  "Hand" = "Hand",
  "Hat" = "Hat",
}

const attributes = Object.values(Attribute);

// need to import the images so they can be used for small screen trait selector
const backgroundImagesPerIndex = {
  0: backgroundImage0,
  1: backgroundImage1,
  2: backgroundImage2,
  3: backgroundImage3,
  4: backgroundImage4,
  5: backgroundImage5,
};

export type Character = { [key in Attribute]: number };

const traitsPerAttribute: Character = {
  [Attribute.Background]: 6,
  [Attribute.Base]: 3,
  [Attribute.Clothes]: 5,
  [Attribute.Face]: 6,
  [Attribute.Hand]: 6,
  [Attribute.Hat]: 7,
};

const getInitialCharacter = (
  randomCharacter: Character,
  searchParams: URLSearchParams
): Character => ({
  [Attribute.Background]:
    Number(searchParams.get(Attribute.Background)) ||
    randomCharacter.Background,
  [Attribute.Base]:
    Number(searchParams.get(Attribute.Base)) || randomCharacter.Base,
  [Attribute.Clothes]:
    Number(searchParams.get(Attribute.Clothes)) || randomCharacter.Clothes,
  [Attribute.Face]:
    Number(searchParams.get(Attribute.Face)) || randomCharacter.Face,
  [Attribute.Hand]:
    Number(searchParams.get(Attribute.Hand)) || randomCharacter.Hand,
  [Attribute.Hat]:
    Number(searchParams.get(Attribute.Hat)) || randomCharacter.Hat,
});

const Generator = () => {
  const [attribute, setAttribute] = useState(Attribute.Background);
  const getRandomCharacter = (): Character => {
    const newCharacter: Character = {
      [Attribute.Background]: Math.floor(
        Math.random() * traitsPerAttribute[Attribute.Background]
      ),
      [Attribute.Base]: Math.floor(
        Math.random() * traitsPerAttribute[Attribute.Base]
      ),
      [Attribute.Clothes]: Math.floor(
        Math.random() * traitsPerAttribute[Attribute.Clothes]
      ),
      [Attribute.Face]: Math.floor(
        Math.random() * traitsPerAttribute[Attribute.Face]
      ),
      [Attribute.Hand]: Math.floor(
        Math.random() * traitsPerAttribute[Attribute.Hand]
      ),
      [Attribute.Hat]: Math.floor(
        Math.random() * traitsPerAttribute[Attribute.Hat]
      ),
    };

    if (newCharacter[Attribute.Base] === 2) {
      newCharacter[Attribute.Hand] = Math.floor(Math.random() * 3);
    } else {
      newCharacter[Attribute.Hand] = Math.floor(Math.random() * 3) + 3;
    }
    return newCharacter;
  };
  const [searchParams, setSearchParams] = useSearchParams("");
  const [character, setCharacter] = useState<Character>(
    getInitialCharacter(getRandomCharacter(), searchParams)
  );
  const characterParams = attributes
    .map((attr) => `${attr}=${character[attr]}`)
    .join("&");
  const linkRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (characterParams && !searchParams.get("code")) {
      setSearchParams(characterParams);
    }
  }, [characterParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const getThreeTraitsFromAttribute = (index: number): number[] => {
    const traits = Array.from(Array(traitsPerAttribute[attribute]).keys());
    const previousIndex = (index - 1 + traits.length) % traits.length;
    const nextIndex = (index + 1) % traits.length;
    return [traits[previousIndex], traits[index], traits[nextIndex]];
  };

  const decreaseAttribute = () => {
    setCharacter({
      ...character,
      [attribute]:
        character[attribute] > 0
          ? character[attribute] - 1
          : traitsPerAttribute[attribute] - 1,
    });
  };

  const increaseAttribute = () => {
    setCharacter({
      ...character,
      [attribute]:
        character[attribute] < traitsPerAttribute[attribute] - 1
          ? character[attribute] + 1
          : 0,
    });
  };

  const downloadPfp = async () => {
    if (linkRef && linkRef.current) {
      const response = await fetch(
        `${ContractInfo[window.chain].backendUrl}/pfp?${searchParams}`
      );
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      linkRef.current.href = url;
      linkRef.current.download = "ReplyGuy.png";
      linkRef.current.click();
      URL.revokeObjectURL(url);
    }
  };

  return (
    <>
      <Navbar />
      <div className={styles.generator}>
        <div className={styles.pageTitle}>Gm-inator</div>
        <div className={styles.pageSubTitle}>
          Customize your Reply Guy and earn GMs!
        </div>
        <div className={styles.mainSection}>
          <div className={styles.canvasWrapper}>
            <div className={styles.canvas}>
              {attributes.map((attribute) => (
                <img
                  src={require(`../../assets/attributes/${attribute}/${character[attribute]}.png`)}
                  alt={attribute}
                  className={styles.characterTrait}
                  key={attribute}
                />
              ))}
            </div>
          </div>
          <div className={styles.traitSelectorWrapper}>
            <div className={styles.traitSelector}>
              <div className={styles.tabs}>
                {attributes.map((a) => (
                  <div
                    key={a}
                    onClick={() => setAttribute(a)}
                    className={`${styles.tab} ${
                      attribute === a ? styles.active : ""
                    }`}
                  >
                    {capitalize(a)}
                  </div>
                ))}
              </div>
              <div className={styles.traits}>
                {Array.from(Array(traitsPerAttribute[attribute]).keys()).map(
                  (trait) => (
                    <img
                      key={trait}
                      src={require(`../../assets/attributes/${attribute}/${trait}.png`)}
                      alt={attribute}
                      width={100}
                      height={100}
                      onClick={() => {
                        setCharacter({
                          ...character,
                          [attribute]: trait,
                        });
                      }}
                    />
                  )
                )}
              </div>
            </div>
            <div className={styles.buttons}>
              <Gminator character={character} />
              <button
                className={styles.primaryButton}
                onClick={() => setCharacter(getRandomCharacter())}
              >
                Randomize!
              </button>
              <a ref={linkRef} style={{ display: "none" }} href="https://none">
                undisplayed
              </a>
              <button className={styles.shareButton} onClick={downloadPfp}>
                Download
              </button>
            </div>
          </div>
          <div className={styles.smallScreenTraitSelector}>
            <select
              className={styles.attributeSelect}
              value={attribute}
              onChange={(e) => setAttribute(e.target.value as Attribute)}
            >
              {attributes.map((attr) => (
                <option key={attr} value={attr}>
                  {attr}
                </option>
              ))}
            </select>
            <div className={styles.smallScreenTraits}>
              <ArrowLeftIcon
                className={styles.arrow}
                onClick={decreaseAttribute}
              />
              {getThreeTraitsFromAttribute(character[attribute]).map(
                (trait) => (
                  <div
                    className={styles.traitSelectorImage}
                    style={{
                      backgroundImage: `url(${
                        // @ts-ignore-next-line
                        backgroundImagesPerIndex[
                          character[Attribute.Background]
                        ]
                      })`,
                    }}
                    key={Math.random()}
                  >
                    <img
                      src={require(`../../assets/attributes/${attribute}/${trait}.png`)}
                      alt={`${attribute}-${trait}`}
                      // className={styles.traitSelectorImage}
                      onClick={() =>
                        setCharacter({
                          ...character,
                          [attribute]: trait,
                        })
                      }
                    />
                  </div>
                )
              )}
              <ArrowRightIcon
                className={styles.arrow}
                onClick={increaseAttribute}
              />
            </div>
          </div>
        </div>
      </div>
      <Footer displayPhone={false} />
    </>
  );
};

export default Generator;
